// hook services
// components
import { SideNavigationLink } from "../../../../../components/SideNavigation";

import { useCompanySettings, useEasyAddReviews, useRbacEmployeePermissions, useUser } from "../../../../../api/hooks";

//utils
import getPossessiveNameCase from "../../../../../utils/getPossessiveNameCase";

//hooks
import useUserReviewsNavigationParams from "../useUserReviewsNavigtionParams";

// Here we parse user info and permissions to get available tabs on the user reviews page
export const getCompanyReviewsNavigationLinks = (
	userId: string,
	employeeName?: string,
	hasDirectReports = false,
	rbac: string[] = []
): SideNavigationLink[] => {
	const navLinks = [];
	if (rbac.includes("reviews:viewUserFormsToDo")) {
		navLinks.push({
			label: `Forms ${employeeName ? `${employeeName} Needs` : "I Need"} To Do`,
			href: `/reviews/${userId}/forms-i-need-to-do/`,
			key: "forms-i-need-to-do",
		});
	}
	if (rbac.includes("reviews:viewUserFormsIDid")) {
		navLinks.push({
			label: `Forms ${employeeName || "I"} Did`,
			href: `/reviews/${userId}/forms-i-did/`,
			key: "forms-i-did",
		});
	}
	if (rbac.includes("reviews:viewUserReviews")) {
		navLinks.push({
			label: `Reviews of ${employeeName || "Me"}`,
			href: `/reviews/${userId}/reviews-of-me/`,
			key: "reviews-of-me",
		});
	}

	if (hasDirectReports && rbac.includes("reviews:viewUserTeamReviews")) {
		navLinks.push({
			label: `${employeeName ? getPossessiveNameCase(employeeName) : "My"} Team`,
			href: `/reviews/${userId}/reviews-of-my-team/`,
			key: "reviews-of-my-team",
		});
	}

	return navLinks;
};

// Here we get user reviews navigation parameters and permissions
const useUserReviewsNavLinksAndPermissions = () => {
	const {
		activePage,
		isLoading: isNavParamsLoading,
		userId = "",
		employeeId = "",
	} = useUserReviewsNavigationParams();
	const { data: rbac, isLoading: isRbacLoading } = useRbacEmployeePermissions(employeeId, "review", !employeeId);
	const { data: employee, isLoading: isUserLoading } = useUser({ userID: employeeId, disabled: !employeeId });
	const { data: companySettings, isLoading: isCompanySettingsLoading } = useCompanySettings();
	const isEasyAddEnabled = !!companySettings?.functions?.reviews?.easy_add;
	const { data: easyAddData, isLoading: isEasyAddLoading } = useEasyAddReviews(
		userId,
		employeeId || userId,
		{ limit: 10, skip: 0 },
		!userId
	);

	const firstName = userId !== employeeId ? employee?.name.split(" ")[0] : null;

	const isLoading =
		isNavParamsLoading || isRbacLoading || isUserLoading || isEasyAddLoading || isCompanySettingsLoading;
	const navLinks = getCompanyReviewsNavigationLinks(employeeId, firstName, employee?.directReports?.length > 0, rbac);

	return {
		isLoading,
		//active tab
		activePage,
		// available navlinks/tabs
		navLinks,
		// employee being viewed
		employeeId,
		// user viewing the page
		userId,
		// employee being viewed direct reports
		directReports: employee?.directReports || [],
		isEasyAddEnabled,
		userCanCreateReviews: rbac?.includes("reviews:create"),
		userCanAdd360Forms: easyAddData?.total_count > 0 && isEasyAddEnabled,
		userCanViewFormsINeedToDo: rbac?.includes("reviews:viewUserFormsToDo"),
		userCanViewFormsIDid: rbac?.includes("reviews:viewUserFormsIDid"),
		userCanViewReviewsOfMe: rbac?.includes("reviews:viewUserReviews"),
		userCanViewReviewsOfMyTeam: rbac?.includes("reviews:viewUserTeamReviews"),
	};
};

export default useUserReviewsNavLinksAndPermissions;
